import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  MapPin, Cloud, ChevronUp, ChevronDown, Loader2, Star, 
  Navigation, Info, Phone, Globe, Clock 
} from 'lucide-react';

function GymResult({ gym, index, details, isExpanded, isLoading, onGetDirections, onFetchDetails }) {
  return (
    <div 
      className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden"
      data-testid={`gym-result-${index}`}
    >
      {/* Main Info */}
      <div className="p-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h5 className="text-white font-medium">{gym.name}</h5>
            <p className="text-slate-400 text-sm">{gym.address}</p>
            {gym.rating && (
              <div className="flex items-center gap-1 mt-1">
                <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
                <span className="text-amber-400 text-sm">{gym.rating}</span>
                {gym.review_count && (
                  <span className="text-slate-500 text-xs">({gym.review_count} reviews)</span>
                )}
              </div>
            )}
            {(gym.is_open !== undefined || gym.open_now !== undefined) && (
              <span className={`text-xs ${(gym.is_open || gym.open_now) ? 'text-green-400' : 'text-red-400'}`}>
                {(gym.is_open || gym.open_now) ? '● Open now' : '● Closed'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* Directions Button */}
            <button
              onClick={() => onGetDirections(gym)}
              className="p-2 bg-green-500/20 rounded-lg hover:bg-green-500/30 transition-colors"
              title="Get Directions"
            >
              <Navigation className="h-4 w-4 text-green-400" />
            </button>
            {/* More Details Button */}
            {gym.place_id && (
              <button
                onClick={() => onFetchDetails(gym.place_id)}
                className="flex items-center gap-1 px-2 py-1.5 bg-blue-500/20 rounded-lg hover:bg-blue-500/30 transition-colors text-xs text-blue-400"
                title="More Info"
              >
                {isLoading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <>
                    {isExpanded ? (
                      <>
                        <ChevronUp className="h-3.5 w-3.5" />
                        Less
                      </>
                    ) : (
                      <>
                        <Info className="h-3.5 w-3.5" />
                        Details
                      </>
                    )}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Expanded Details */}
      {isExpanded && details && !details.error && (
        <div className="px-3 pb-3 pt-0 border-t border-slate-700/50 space-y-2">
          {/* Phone */}
          {details.phone && (
            <a 
              href={`tel:${details.phone}`}
              className="flex items-center gap-2 text-sm text-slate-300 hover:text-blue-400 transition-colors"
            >
              <Phone className="h-4 w-4 text-cyan-400" />
              {details.phone}
            </a>
          )}
          
          {/* Website */}
          {details.website && (
            <a 
              href={details.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-slate-300 hover:text-blue-400 transition-colors"
            >
              <Globe className="h-4 w-4 text-violet-400" />
              <span className="truncate">{details.website.replace(/^https?:\/\//, '').split('/')[0]}</span>
            </a>
          )}
          
          {/* Google Maps */}
          {details.google_maps_url && (
            <a 
              href={details.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-slate-300 hover:text-blue-400 transition-colors"
            >
              <MapPin className="h-4 w-4 text-red-400" />
              Open in Google Maps
            </a>
          )}
          
          {/* Opening Hours */}
          {details.opening_hours && details.opening_hours.length > 0 && (
            <div className="mt-2">
              <div className="flex items-center gap-2 text-sm text-slate-400 mb-1">
                <Clock className="h-4 w-4 text-amber-400" />
                Opening Hours
              </div>
              <div className="pl-6 space-y-0.5">
                {details.opening_hours.map((hours, i) => (
                  <p key={i} className="text-xs text-slate-500">{hours}</p>
                ))}
              </div>
            </div>
          )}
          
          {/* No details available */}
          {!details.phone && !details.website && !details.google_maps_url && (!details.opening_hours || details.opening_hours.length === 0) && (
            <p className="text-slate-500 text-sm">No additional details available</p>
          )}
        </div>
      )}
    </div>
  );
}

export function GymFinderCard({
  showButton = true,
  showGymFinder,
  setShowGymFinder,
  gymSearchQuery,
  setGymSearchQuery,
  searchingGyms,
  nearbyGyms,
  gymDetails,
  expandedGymId,
  loadingGymDetails,
  onSearchGyms,
  onFindNearbyGyms,
  onFetchGymDetails,
  onGetDirections,
}) {
  return (
    <div className={showButton ? 'mt-6' : ''}>
      {showButton && (
      <button
        onClick={() => setShowGymFinder(!showGymFinder)}
        className="w-full group bg-gradient-to-br from-cyan-500/10 to-blue-600/10 backdrop-blur-sm border-2 border-cyan-500/30 hover:border-cyan-400/50 rounded-3xl p-5 transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 hover:shadow-xl hover:shadow-cyan-500/20"
        data-testid="gym-finder-btn"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/30">
            <MapPin className="w-7 h-7 text-white icon-pulse" />
          </div>
          <div className="text-left flex-1">
            <h3 className="text-lg font-heading font-bold text-white group-hover:text-cyan-400 transition-colors">
              Gym Finder
            </h3>
            <p className="text-slate-400 text-sm flex items-center gap-1">
              <Cloud className="h-3 w-3" /> 
              If weather is bad, find a gym nearby
            </p>
          </div>
          {showGymFinder ? (
            <ChevronUp className="w-5 h-5 text-cyan-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400 group-hover:text-cyan-400" />
          )}
        </div>
      </button>
      )}
      
      {/* Gym Finder Expanded Content */}
      {showGymFinder && (
        <Card className="mt-4 bg-slate-900/80 border-slate-800 rounded-2xl">
          <CardContent className="p-4 space-y-4">
            <p className="text-slate-400 text-sm">
              Can't walk outside due to bad weather? Find a nearby gym to continue your workout indoors!
            </p>
            
            <div className="flex gap-2">
              <Input
                placeholder="Search gyms by city or area..."
                value={gymSearchQuery}
                onChange={(e) => setGymSearchQuery(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
                onKeyPress={(e) => e.key === 'Enter' && onSearchGyms()}
                data-testid="gym-search-input"
              />
              <Button
                onClick={onSearchGyms}
                disabled={searchingGyms || !gymSearchQuery.trim()}
                className="bg-cyan-600 hover:bg-cyan-500"
                data-testid="gym-search-btn"
              >
                {searchingGyms ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <MapPin className="h-4 w-4" />
                )}
              </Button>
            </div>
            
            <Button
              onClick={onFindNearbyGyms}
              disabled={searchingGyms}
              variant="outline"
              className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="gym-location-btn"
            >
              {searchingGyms ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Finding gyms...
                </>
              ) : (
                <>
                  <MapPin className="h-4 w-4 mr-2" />
                  Use My Location
                </>
              )}
            </Button>
            
            {/* Gym Results */}
            {nearbyGyms.length > 0 && (
              <div className="space-y-3 mt-4">
                <h4 className="text-white font-medium">Nearby Gyms:</h4>
                {nearbyGyms.map((gym, index) => (
                  <GymResult
                    key={index}
                    gym={gym}
                    index={index}
                    details={gymDetails[gym.place_id]}
                    isExpanded={expandedGymId === gym.place_id}
                    isLoading={loadingGymDetails === gym.place_id}
                    onGetDirections={onGetDirections}
                    onFetchDetails={onFetchGymDetails}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default GymFinderCard;
